<script setup lang="ts">
/**
 * Панель версий — макет 23:53: колонка 320 справа от редактора.
 *
 * Версия — снимок, который человек сделал сам кнопкой «Сохранить версию»;
 * автосейв их не плодит. Поэтому «текущая» — это не всегда самая свежая
 * запись: после снимка текст правят дальше, и тогда ни одна версия не
 * совпадает с тем, что на экране.
 *
 * Имени автора в панели нет: knowledge отдаёт только идентификатор, а ручки
 * «кто это» у фронта не существует (docs/design-gaps.md).
 */
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import { formatStamp } from '@/features/issues/format'

import type { PageVersion } from '../api'

const props = defineProps<{
  versions: PageVersion[]
  /** Текст страницы сейчас — им отличаем снимок, совпадающий с экраном */
  title: string
  contentMd: string
  openedNumber?: number
}>()

const emit = defineEmits<{ open: [PageVersion]; restore: [PageVersion] }>()

const { t, locale } = useI18n()

/** Свежие сверху: историю читают с конца, а не с начала. */
const ordered = computed(() => [...props.versions].sort((a, b) => b.number - a.number))

const isCurrent = (version: PageVersion) =>
  version.title === props.title && version.contentMd === props.contentMd

const when = (version: PageVersion) =>
  formatStamp(version.createdAt, locale.value, {
    today: t('issue.today'),
    yesterday: t('issue.yesterday'),
  })
</script>

<template>
  <aside class="vpanel">
    <header class="vpanel__head">{{ t('kb.versions.title') }}</header>

    <p v-if="!ordered.length" class="vpanel__empty">{{ t('kb.versions.empty') }}</p>

    <article
      v-for="version in ordered"
      :key="version.id"
      class="vrow"
      :class="{ 'vrow--current': isCurrent(version), 'vrow--opened': version.number === openedNumber }"
    >
      <p class="vrow__number">
        {{ t('kb.versions.number', { number: version.number }) }}
        <span v-if="isCurrent(version)"> · {{ t('kb.versions.current') }}</span>
      </p>
      <p class="vrow__when">{{ when(version) }}</p>
      <p v-if="version.comment" class="vrow__comment">{{ version.comment }}</p>

      <p v-if="!isCurrent(version)" class="vrow__actions">
        <button type="button" class="vrow__action" @click="emit('open', version)">
          {{ t('kb.versions.open') }}
        </button>
        <span class="vrow__dot">·</span>
        <button type="button" class="vrow__action" @click="emit('restore', version)">
          {{ t('kb.versions.restore') }}
        </button>
      </p>
    </article>
  </aside>
</template>

<style scoped>
.vpanel {
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  width: 320px;
  overflow-y: auto;
  border-left: 1px solid var(--color-border);
  background: var(--color-surface);
}

.vpanel__head {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  height: 44px;
  padding: 0 16px;
  background: var(--color-bg-muted);
  color: var(--color-text-muted);
  font-size: var(--font-size-overline);
  font-weight: var(--font-weight-semibold);
  letter-spacing: 0.6px;
  text-transform: uppercase;
}

.vpanel__empty {
  margin: 0;
  padding: 16px;
  color: var(--color-text-muted);
  font-size: var(--font-size-body-sm);
}

.vrow {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 10px 16px;
  border-bottom: 1px solid var(--color-border);
}

.vrow--current {
  background: var(--color-primary-tint);
}

/* Открытая на просмотр версия подсвечена рамкой, а не заливкой: заливка уже
   занята текущей, и две одинаковые подложки читались бы как одно и то же. */
.vrow--opened {
  box-shadow: inset 2px 0 0 var(--color-primary);
}

.vrow__number {
  margin: 0;
  color: var(--color-text-primary);
  font-size: var(--font-size-body-sm);
  font-weight: var(--font-weight-semibold);
}

.vrow--current .vrow__number {
  color: var(--color-primary);
}

.vrow__when,
.vrow__comment {
  margin: 0;
  color: var(--color-text-muted);
  font-size: var(--font-size-caption);
}

.vrow__comment {
  color: var(--color-text-secondary);
}

.vrow__actions {
  display: flex;
  gap: 4px;
  margin: 0;
}

.vrow__action {
  padding: 0;
  border: none;
  background: none;
  color: var(--color-primary);
  font-family: var(--font-family-sans);
  font-size: var(--font-size-caption);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
}

.vrow__action:hover {
  text-decoration: underline;
}

.vrow__dot {
  color: var(--color-text-muted);
  font-size: var(--font-size-caption);
}
</style>
